import { QueryClient, QueryKey } from '@tanstack/react-query';
import { produce } from 'immer';

import meKeys from '@/api/queries/me/keyFactory';
import { MyAccountTeamsQuery, MyAccountUsersQuery } from '@/gql/graphql';

/**
 * Snapshot of the account-users caches taken before an optimistic membership
 * change, used to roll back if the mutation fails.
 */
export type MembersMutationContext = {
  previousUsers: [QueryKey, MyAccountUsersQuery | undefined][];
};

type MembershipChange = {
  teamId: string;
  userId: string;
  action: 'add' | 'remove';
};

const teamNameFromCache = (
  queryClient: QueryClient,
  teamId: string
): string => {
  const teams = queryClient.getQueryData<MyAccountTeamsQuery>(
    meKeys.accountTeams()
  );
  return (
    teams?.me.user.account.teams.items.find((team) => team.id === teamId)
      ?.name ?? ''
  );
};

const applyChange = (
  data: MyAccountUsersQuery | undefined,
  { teamId, userId, action }: MembershipChange,
  teamName: string
): MyAccountUsersQuery | undefined =>
  data &&
  produce(data, (draft) => {
    const user = draft.me.user.account.users.find(
      (candidate) => candidate.publicId === userId
    );
    if (!user) {
      return;
    }

    if (action === 'remove') {
      user.teams.items = user.teams.items.filter((team) => team.id !== teamId);
    } else if (!user.teams.items.some((team) => team.id === teamId)) {
      user.teams.items.push({ id: teamId, name: teamName });
    }
  });

/**
 * Optimistically reflects a team membership change across the account-users
 * caches so the Users page and Manage members modal update immediately.
 * Returns the pre-change snapshot for {@link rollbackOptimisticMembership}.
 */
export const applyOptimisticMembership = async (
  queryClient: QueryClient,
  change: MembershipChange
): Promise<MembersMutationContext> => {
  await queryClient.cancelQueries({ queryKey: meKeys.accountUsers() });

  const previousUsers = queryClient.getQueriesData<MyAccountUsersQuery>({
    queryKey: meKeys.accountUsers(),
  });

  const teamName = teamNameFromCache(queryClient, change.teamId);

  queryClient.setQueriesData<MyAccountUsersQuery>(
    { queryKey: meKeys.accountUsers() },
    (data) => applyChange(data, change, teamName)
  );

  return { previousUsers };
};

export const rollbackOptimisticMembership = (
  queryClient: QueryClient,
  context: MembersMutationContext | undefined
): void => {
  context?.previousUsers.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });
};
